import React from 'react';
import { Callout, DocSection, Eyebrow, FactGrid, InvestorsContactCta, InvestorsSubnav } from './InvestorsShared';

const eraRows = [
  {
    era: '2026 to 2028',
    name: 'The agent cloud',
    body: 'Agents become the main users of developer infrastructure. We win the account they bill against: repos, routing, compute, hosting, and editors on one balance. Every product is also a tool an agent can call.',
  },
  {
    era: '2028 to 2031',
    name: 'Infrastructure that optimizes itself',
    body: 'The router learns. It picks the model and the GPU per request on price, latency, and quality, across every provider, in about a millisecond. Customers stop making infrastructure decisions.',
  },
  {
    era: '2031 to 2036',
    name: 'Software that maintains itself',
    body: 'Systems watch their own telemetry, file their own issues, propose and test fixes, and merge through review gates humans set. Long-running agents operate whole software estates on the platform.',
  },
];

export const InvestorsVisionPage: React.FC = () => (
  <main className="overflow-hidden bg-black text-white">
    <section className="relative border-b border-white/10 px-4 pb-12 pt-24 sm:px-8 lg:px-12">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(56,189,248,0.10),transparent_55%)]" />
      <div className="relative mx-auto max-w-3xl">
        <Eyebrow>Investors</Eyebrow>
        <h1 className="mt-4 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl">
          The 10-year vision
        </h1>
        <p className="mt-5 text-base leading-8 text-white/72">
          Where we think software goes in the next decade, and what we are building for it.
        </p>
        <div className="mt-8">
          <InvestorsSubnav active="/investors/vision" />
        </div>
      </div>
    </section>

    <div className="px-4 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-3xl space-y-12 py-12">
        <DocSection id="premise" title="The premise">
          <p>
            Software has always been limited by the number of people who can write it. Compilers, open
            source, and cloud each removed one bottleneck and grew the industry. Language models remove
            the last one: the human in the inner loop of writing code.
          </p>
          <p>
            When code costs about what the compute costs, the amount of software grows sharply, most of
            it written and maintained by agents and never read by a person. Those programs still need
            somewhere to run: repos, models, GPUs, deploys, budgets, and review gates. Writing code stops
            being the scarce part; the loop around it becomes the scarce part.
          </p>
          <Callout label="In one line">
            <p>
              app.nz runs that loop, from intent to running system. Whoever runs it owns the account
              everything bills to.
            </p>
          </Callout>
        </DocSection>

        <DocSection id="opportunity" title="Why the opportunity is large">
          <p>
            Developer infrastructure is several large markets: code hosting, CI, compute, GPU inference,
            model APIs, observability, and creative tools. Each was sized for human developers. Agents
            break that sizing in two ways.
          </p>
          <p>
            First, agents multiply builders. One customer runs dozens of agents around the clock, each
            consuming repos, compute, model calls, and deploys. Usage grows with agent count, which grows
            with model capability.
          </p>
          <p>
            Second, agents cross market boundaries. One task edits a repo, calls three models, renders an
            image, runs a GPU job, and ships a deploy. Spreading that over six vendors is friction, and
            agents route around friction.
          </p>
          <p>
            We do not need to displace incumbents. We need to be where new, agent-first workloads land,
            because that spend grows fastest.
          </p>
          <FactGrid
            items={[
              { v: 'Seats to usage', k: 'pricing follows agent work, not headcount' },
              { v: '24/7', k: 'agents use infrastructure around the clock' },
              { v: '6 to 1', k: 'agents consolidate vendors' },
              { v: 'Compounding', k: 'demand grows with model capability' },
            ]}
          />
        </DocSection>

        <DocSection id="what-we-are" title="What app.nz is today">
          <p>
            All of this is live and billing customers. Hosted repos with branch previews and PR review.
            Coding agents that branch, edit, run tests, and open pull requests. An OpenAI-compatible
            gateway across 20+ model providers under one key. GPU inference with scale-to-zero
            endpoints, fine-tuning, and container builds. App and site hosting with free HTTPS
            subdomains. Twelve editors for video, audio, image, vector, slides, sheets, docs, 3D,
            animation, and whiteboard, sharing one asset store and one credit balance.
          </p>
          <p>
            We run our own products on it. app.nz/papers, readingtime.app.nz, helix.app.nz, and
            gpubrain.app.nz use the same repos, agents, gateway, and hosting we sell. When the platform
            breaks, our products break first.
          </p>
          <p>
            The breadth is deliberate. No single surface wins on its own, but together they are the one
            thing nobody else sells: the whole loop on one account, on one prepaid balance an agent can
            be trusted to spend.
          </p>
        </DocSection>

        <DocSection id="decade" title="The decade in three stages">
          <div className="space-y-4">
            {eraRows.map((e) => (
              <div key={e.era} className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-sky-400">{e.era}</p>
                <h3 className="mt-2 font-display text-xl font-bold text-white">{e.name}</h3>
                <p className="mt-2 text-sm leading-7 text-white/68">{e.body}</p>
              </div>
            ))}
          </div>
          <p>
            The stages overlap, and parts of stage three are in research now. Stage one earns the
            account. Stage two earns the margin, because a router that saves customers money on every
            request is one they keep using. Stage three earns the category.
          </p>
        </DocSection>

        <DocSection id="auto-infra" title="Infrastructure that optimizes itself">
          <p>
            Teams pin a model because switching is risky, overpay for reserved GPUs because spot is
            fiddly, and leave latency on the table because benchmarking is a chore. A router should make
            those calls per request instead.
          </p>
          <p>
            The gateway already routes across 20+ providers behind lanes like <code>app/auto</code>,{' '}
            <code>app/auto-code</code>, and <code>app/auto-fast</code>. The next version is a learned
            router with about a millisecond to decide. It embeds the request, checks live price, latency,
            and health from every provider, and places the work on the best model and the cheapest
            capable GPU: serverless for a burst, a hosted pod for steady traffic, spot when the
            interruption math works.
          </p>
          <p>
            Every request teaches the router. A better router wins more traffic on price and quality.
            More traffic buys better capacity pricing and more training signal. That loop is the margin.
          </p>
        </DocSection>

        <DocSection id="every-medium" title="An editor for every medium">
          <p>
            Agents do not stop at pull requests. The customer who asks an agent to fix CI also asks it to
            cut a launch video, build a deck, retexture a 3D asset, and record a voiceover. Existing
            creative tools bolt AI onto file formats made for one person. Our editors treat the agent as
            a collaborator: every operation is a tool, every asset lives in shared storage, every render
            bills the same balance.
          </p>
          <p>
            Twelve editors on one substrate beat twelve products, because agents chain them. A marketing
            agent that writes the copy, draws the diagrams, cuts the video, and ships the landing page
            needs each step to be an API call on one account.
          </p>
        </DocSection>

        <DocSection id="self-organizing" title="Software that maintains itself">
          <p>
            Self-organizing software is one loop: telemetry, detection, proposal, proof, gated merge,
            deploy. The system notices its own regression, writes the fix, proves it with tests and
            benchmarks, and ships it through a review gate a person configured once. Ownership moves from
            who wrote the code to who set the goals and constraints.
          </p>
          <p>
            Each part of the loop exists today: repos and PRs (proposal), CI and VisualBench (proof),
            auto-agents (detection), deploys with previews (shipping), budgets and permissions
            (governance). We run early versions on our own estate, where agents file and fix real issues
            on the platform that hosts them.
          </p>
          <p>
            Three problems stand between here and that category: verification strong enough to trust,
            goals that stay stable over thousands of iterations, and review gates that still mean
            something when proposals arrive faster than people read them. These are platform problems,
            solved with better proof machinery and better gates as much as better models.
          </p>
          <Callout label="Why a platform wins this">
            <p>
              A self-maintaining system needs the repos, the telemetry, the compute, the deploys, and the
              governance in one place. An IDE or a model API cannot do that alone.
            </p>
          </Callout>
        </DocSection>

        <DocSection id="run-forever" title="Agents that run for months">
          <p>
            Today's agents are sprinters: spawn, task, PR, exit. The valuable ones will run for months.
            Watching a dependency tree for CVEs and patching them. Holding a service's p99 under target
            by moving its infrastructure nightly. Running a storefront's content pipeline end to end.
          </p>
          <p>
            A long-running agent is a customer that never sleeps and rarely churns. It is also the
            hardest workload to host. It needs persistent memory, resumable execution, spend limits,
            visible behaviour, and an owner who can always pull the cord. Each of those is a product.
            Schedulers, auto-agents, and queues are live now; memory and escalation are next.
          </p>
        </DocSection>

        <DocSection id="why-us" title="Why us">
          <p>
            We are a small, technical, founder-led team in New Zealand. We have shipped a lot of surface
            area because we build with the product we sell: the agents write the platform, and the
            platform hosts the agents. That is our development speed, our best demo, and the proof the
            thesis works.
          </p>
          <p>
            We build in the open: public repos, public docs, public benchmark methodology. Every claim
            here is one demo away from being checked, and being checkable earns trust faster than a
            better deck.
          </p>
          <p>
            We are capital-efficient by design. The routing we sell also runs our own fleet.
            Scale-to-zero keeps idle surface close to free. Agents do work that would otherwise be hires.
          </p>
        </DocSection>

        <DocSection id="beliefs" title="What we believe">
          <ul className="list-disc space-y-3 pl-5">
            <li>
              <strong className="text-white">Agents are customers.</strong> Build every surface so an
              agent with a budget can operate it, and people get a better product too.
            </li>
            <li>
              <strong className="text-white">The loop is the moat.</strong> Any single feature can be
              copied. The whole loop on one account cannot be copied piece by piece.
            </li>
            <li>
              <strong className="text-white">Routing is margin.</strong> Every decision the platform
              makes better than a person, on model, GPU, or timing, is margin that grows with traffic.
            </li>
            <li>
              <strong className="text-white">Governance makes autonomy sellable.</strong> Budgets, gates,
              and audit trails are what let customers say yes.
            </li>
            <li>
              <strong className="text-white">Build in the open.</strong> Checkable claims beat polished
              decks.
            </li>
          </ul>
          <p>
            The <a href="/investors/deep-dive" className="font-semibold text-sky-400 hover:text-sky-300">technical deep dive</a>{' '}
            covers architecture, security, economics, benchmarks, and a dated roadmap.
          </p>
        </DocSection>

        <InvestorsContactCta />
      </div>
    </div>
  </main>
);

export default InvestorsVisionPage;
